import { ORGANIZATION_UNAVAILABLE_NOTICE_KEY } from '@/constants/storage-keys';
import {
  clearTabOrganizationId,
  getTabOrganizationId,
  whenThisPageLeaves,
} from '@/lib/current-organization';

const UNAVAILABLE_CODE = 'active_organization_unavailable';
const REQUIRED_CODE = 'active_organization_required';

interface OrganizationUnavailableNotice {
  title: string;
  detail: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// The typed client reads every body as text, so a problem can arrive still serialized.
const problemOf = (body: unknown): Record<string, unknown> | null => {
  if (isRecord(body)) return body;
  if (typeof body !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(body);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const leave = (notice: OrganizationUnavailableNotice): void => {
  clearTabOrganizationId();
  try {
    window.sessionStorage.setItem(
      ORGANIZATION_UNAVAILABLE_NOTICE_KEY,
      JSON.stringify(notice),
    );
  } catch {
    /* private mode — the reload still lands in the user's own workspace */
  }
};

const noticeFrom = (
  body: Record<string, unknown>,
): OrganizationUnavailableNotice => ({
  title: typeof body.title === 'string' ? body.title : '',
  detail: typeof body.detail === 'string' ? body.detail : '',
});

// Keyed on the request, not the tab: a concurrent response arrives after the first one cleared it.
export const leaveIfOrganizationUnavailable = (
  requestNamedOrganization: boolean,
  status: number | undefined,
  body: unknown,
): boolean => {
  if (!requestNamedOrganization || status !== 404) return false;
  const problem = problemOf(body);
  if (problem?.code !== UNAVAILABLE_CODE) return false;

  const notice = noticeFrom(problem);
  whenThisPageLeaves(() => leave(notice));
  window.location.reload();
  return true;
};

// A request sent before the tab was pinned; with nothing stored a reload would only loop.
export const reloadIfOrganizationRequired = (
  status: number | undefined,
  body: unknown,
): boolean => {
  if (status !== 400 || problemOf(body)?.code !== REQUIRED_CODE) return false;
  if (getTabOrganizationId() === null) return false;

  window.location.reload();
  return true;
};

export const takeOrganizationUnavailableNotice =
  (): OrganizationUnavailableNotice | null => {
    try {
      const raw = window.sessionStorage.getItem(
        ORGANIZATION_UNAVAILABLE_NOTICE_KEY,
      );
      window.sessionStorage.removeItem(ORGANIZATION_UNAVAILABLE_NOTICE_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      return isRecord(parsed) ? noticeFrom(parsed) : null;
    } catch {
      return null;
    }
  };
